import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function Chat() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const toParam = params.get("to");
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(toParam);
  const [activeUser, setActiveUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState(params.get("msg") || "");
  const endRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/conversations").then(r=>setConvs(r.data));
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    if (!active) return;
    const load = async () => {
      const r = await api.get(`/messages/${active}`);
      setMessages(r.data);
      try { const p = await api.get(`/profile/${active}`); setActiveUser(p.data); } catch {}
    };
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [active]);

  useEffect(()=>{ endRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    try { await api.post("/messages", { toUserId: active, body: text }); }
    catch (err) { toast.error(err?.response?.data?.detail || "Messaggio non inviato"); return; }
    setText("");
    const r = await api.get(`/messages/${active}`); setMessages(r.data);
  };

  if (!user) return <div className="max-w-3xl mx-auto px-6 py-16 text-center"><p className="text-slate-600">Accedi per usare la chat.</p><Link to="/login" className="btn-pill-gold inline-flex mt-4">Accedi</Link></div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10 grid md:grid-cols-[320px_1fr] gap-6 h-[70vh]">
      <aside className="card-premium p-4 overflow-y-auto">
        <div className="overline text-gold mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4"/>Conversazioni</div>
        {convs.length===0 && !active && <p className="text-sm text-slate-500">Nessuna conversazione. Apri una chat dalla scheda di un artista o fornitore.</p>}
        {convs.map((c)=>(
          <button key={c.conversationId} data-testid={`conv-${c.otherUser?.id}`} onClick={()=>setActive(c.otherUser?.id)} className={`w-full text-left p-3 rounded-xl mb-1 ${active===c.otherUser?.id?"bg-gold/10 border border-gold/40":"hover:bg-slate-50"}`}>
            <div className="font-semibold text-sm">{c.otherUser?.companyName || `${c.otherUser?.firstName||""} ${c.otherUser?.lastName||""}`}</div>
            <div className="text-xs text-slate-500 truncate">{c.lastMessage}</div>
          </button>
        ))}
      </aside>
      <section className="card-premium flex flex-col overflow-hidden">
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-slate-500 text-sm">Seleziona una conversazione</div>
        ) : (
          <>
            <header className="p-4 border-b border-slate-200 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-navy text-gold flex items-center justify-center font-bold">{(activeUser?.companyName||"?")[0]}</div>
              <div>
                <div className="font-semibold">{activeUser?.companyName || "Conversazione"}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">{activeUser?.role}</div>
              </div>
            </header>
            <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50">
              {messages.map((m)=>(
                <div key={m.id} className={`max-w-[70%] p-3 rounded-2xl text-sm ${m.fromUserId===user.id?"ml-auto bg-navy text-white":"bg-white border border-slate-200"}`}>
                  {m.body}
                </div>
              ))}
              <div ref={endRef}/>
            </div>
            <form onSubmit={send} className="p-3 border-t border-slate-200 flex gap-2">
              <input data-testid="chat-input" value={text} onChange={(e)=>setText(e.target.value)} placeholder="Scrivi un messaggio…" className="flex-1 border border-slate-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-gold"/>
              <button data-testid="chat-send" className="btn-pill-gold text-sm"><Send className="w-4 h-4"/></button>
            </form>
          </>
        )}
      </section>
    </div>
  );
}
